/**
 * StripeProvider — initializes Stripe.js with the connected-account model.
 *
 * Critical detail (data-layer handoff §10): we MUST pass `stripeAccount` to
 * `loadStripe` when the patient's clinic is a Stripe connected account.
 * Without it, money posts to the platform account instead of the clinic
 * — this was a real production bug in the legacy frontend.
 *
 * Caching strategy: `loadStripe` is heavyweight (loads ~75 KB of JS). We
 * cache the promise **per connected-account-id** so a navigation that
 * remounts this provider doesn't re-load Stripe.js.
 *
 * The `<Elements>` provider here uses the **deferred-intent** mode of the
 * Payment Element. Reasoning: our backend creates the PaymentIntent only
 * after receiving a `stripe_payment_method_id`, but Stripe's classic
 * Elements API needs `clientSecret` upfront. Deferred-intent mode
 * (`mode: 'payment'`, `paymentMethodCreation: 'manual'`) decouples the
 * two — Stripe renders the form against just the amount/currency, and
 * the page calls `stripe.createPaymentMethod({elements})` to get a pm
 * before talking to our backend.
 *
 * See: https://docs.stripe.com/payments/accept-a-payment-deferred
 */
import { useMemo, useState, useEffect, type PropsWithChildren } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';

interface StripeProviderProps {
  publishableKey: string;
  connectedAccountId: string;
  /** Integer cents. The displayed amount the patient is paying. */
  amountCents: number;
  /** "usd" — only USD supported in Phase 0. */
  currency?: 'usd';
}

const stripePromiseCache = new Map<string, Promise<Stripe | null>>();

function getStripePromise(
  publishableKey: string,
  connectedAccountId: string,
): Promise<Stripe | null> {
  const key = `${publishableKey}::${connectedAccountId}`;
  let p = stripePromiseCache.get(key);
  if (!p) {
    p = loadStripe(publishableKey, { stripeAccount: connectedAccountId });
    stripePromiseCache.set(key, p);
  }
  return p;
}

export function StripeProvider({
  publishableKey,
  connectedAccountId,
  amountCents,
  currency = 'usd',
  children,
}: PropsWithChildren<StripeProviderProps>) {
  const stripePromise = useMemo(
    () => getStripePromise(publishableKey, connectedAccountId),
    [publishableKey, connectedAccountId],
  );

  const [debouncedAmount, setDebouncedAmount] = useState(amountCents);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedAmount(amountCents);
    }, 200);
    return () => clearTimeout(handler);
  }, [amountCents]);

  // The `options` for Elements drive the Payment Element's behavior.
  // `mode: 'payment'` + `paymentMethodCreation: 'manual'` is the deferred-
  // intent flow. `amount` must be in the smallest unit (cents for USD),
  // matches our internal representation exactly.
  const options = useMemo(
    () => ({
      mode: 'payment' as const,
      amount: Math.max(debouncedAmount, 50),
      currency,
      paymentMethodCreation: 'manual' as const,
      appearance: buildAppearance(),
      // Surface the channels the data-layer doc calls out.
      paymentMethodTypes: ['card'],
    }),
    [debouncedAmount, currency],
  );

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
}

/**
 * Build the appearance options to match Tensaw's design tokens.
 *
 * Stripe's appearance API accepts CSS color strings (not CSS variables),
 * so we read the computed root styles and re-emit hsl() strings. This
 * keeps light/dark theme switches automatic — re-mounting the provider
 * picks up the new values.
 */
function buildAppearance(): { theme: 'stripe'; variables: Record<string, string> } {
  const fallback = {
    colorPrimary: 'hsl(180 40% 35%)',
    colorBackground: '#ffffff',
    colorText: 'hsl(222 47% 11%)',
    colorDanger: 'hsl(0 72% 51%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    borderRadius: '8px',
  };
  if (typeof window === 'undefined') return { theme: 'stripe', variables: fallback };

  try {
    const root = getComputedStyle(document.documentElement);
    const read = (name: string, fb: string) => {
      const v = root.getPropertyValue(name).trim();
      return v ? `hsl(${v})` : fb;
    };
    return {
      theme: 'stripe',
      variables: {
        colorPrimary: read('--primary', fallback.colorPrimary),
        colorBackground: '#ffffff',
        colorText: read('--foreground', fallback.colorText),
        colorDanger: read('--destructive', fallback.colorDanger),
        fontFamily: fallback.fontFamily,
        borderRadius: '8px',
      },
    };
  } catch {
    return { theme: 'stripe', variables: fallback };
  }
}
